import * as Sentry from "@sentry/browser"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, compact, find, get, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  value: any
  originalValue: any
  wordCountChange?: number
  cardStackId: string
  prop: string
  sectionId: string
}

export function prepareLessonCardUpdate(payload: Payload): PreparedAction {
  return {
    name: "LESSON_CARD_UPDATE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.sectionId,
  })
    .set("embedded", `attributes.${payload.prop}`, payload.value)
    .inc("embedded", `attributes._revision`, 1).patch

  return [patch]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.sectionId,
  })
    .set("embedded", `attributes.${payload.prop}`, payload.originalValue)
    .inc("embedded", `attributes._revision`, -1).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let card = find(get(models, "cardStack.attributes.cards"), {
    id: payload.sectionId,
  })
  let cardTitle = get(card, ["attributes", "title"])
  let title = ""
  let titleForAnalytics = ""
  let description = ""
  let context = {
    ...defaultLessonContext(payload, models),
    cardTitle,
  }

  let sharedEventProperties: Pick<
    LessonEditCard,
    | "root_id"
    | "root_type"
    | "date_plan_scheduled_for"
    | "primary_object_id"
    | "primary_object_id_and_ancestor_ids"
    | "primary_verb"
    | "primary_possessive"
    | "primary_object_type"
    | "primary_object_specifier"
    | "adverbial_verb"
    | "adverbial_object_type"
    | "adverbial_object_id"
    | "adverbial_possessive"
    | "adverbial_object_id_and_ancestor_ids"
  > = {
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "cards",

    adverbial_verb: "editing",
    adverbial_object_type: "card",

    adverbial_object_id: payload.sectionId,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }

  let newEvent: LessonEditCard | null = null

  if (payload.prop === "title") {
    title = "Renamed Lesson Card"
    titleForAnalytics = "Lesson Card - Edit Title"
    description = `Changed card title from "${payload.originalValue}" to "${payload.value}"`
    newEvent = {
      ...createBlankEvent(),
      ...sharedEventProperties,
      adverbial_object_specifier: "title",
    }
  } else if (payload.prop === "value") {
    title = "Edited Lesson Card"
    titleForAnalytics = "Lesson Card - Edit Value"
    let deltaInWords
    if (payload.wordCountChange && payload.wordCountChange > 0) {
      deltaInWords =
        payload.wordCountChange === 1
          ? `and added 1 word`
          : `and added ${payload.wordCountChange} words`
    } else if (payload.wordCountChange && payload.wordCountChange < 0) {
      deltaInWords =
        -payload.wordCountChange === 1
          ? `and deleted 1 word`
          : `and deleted ${-payload.wordCountChange} words`
    } else {
      deltaInWords = ""
    }
    description = `Edited ${cardTitle} ${deltaInWords}.`

    let newContext = assign({}, context, {
      wordCountChange: payload.wordCountChange,
    })
    context = newContext
    newEvent = {
      ...createBlankEvent(),
      ...sharedEventProperties,
      adverbial_object_specifier: "value",
    }
  } else if (payload.prop === "color") {
    title = "Changed Lesson Card Color"
    titleForAnalytics = "Lesson Card - Changed Color"
    description = `Changed the color of ${cardTitle}`
    newEvent = {
      ...createBlankEvent(),
      ...sharedEventProperties,
      adverbial_object_specifier: "color",
    }
  }

  if (newEvent === null) {
    Sentry.withScope((scope) => {
      scope.setLevel(Sentry.Severity.Error)
      scope.setExtra("prop", payload.prop)
      Sentry.captureMessage("We updated a property we didn't handle in our analytics")
    })
  }

  return {
    title: title,
    titleForAnalytics: titleForAnalytics,
    description: description,
    context: context,
    activity: defaultLessonActivity(payload, models),
    newAnalyticsEvents: compact([newEvent]),
  }
}
